/* eslint-disable no-restricted-globals */

import { globalCache } from 'utils/cache';
import { useUpdateOnHMR } from 'utils/hmr';
import { Platform } from '../platform/Platform';
import UnknownComponent from './UnknownComponent';
import { baseComponentManager } from './Components';
import { AnyManager, ComponentManager } from './Component.type';
import type { ComponentItemType, ItemType } from './ComponentRegistry';

const ComponentsCache = globalCache.get<Record<string, AnyManager>>(
  'Components',
  (val) => {
    return val ?? {};
  },
);

const ComponentNameCache = globalCache.get<Record<string, string>>(
  'ComponentNameMap',
  (val) => {
    return val ?? {};
  },
);

const CustomChildItemTypesCache = globalCache.get<Record<string, string[]>>(
  'CustomChildItemTypesMap',
  (val) => {
    if (val && Object.keys(val).length) {
      deregisterCustomComponents(false);
    }
    return {};
  },
);

function deregisterCustomComponents(reset = true) {
  if (reset) {
    CustomChildItemTypesCache.reset();
  }
  getAllManagers().forEach((mgr) => {
    if (mgr.version) {
      delete ComponentsCache.value[mgr.type];
      delete ComponentNameCache.value[mgr.type];
    }
  });
}

function registerManager(itemType: string, mgr: AnyManager): void {
  ComponentsCache.value[itemType] = mgr;
  ComponentNameCache.value[itemType] = mgr.name;
}

function registerParentItemTypes(
  itemType: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mgr: ComponentManager<any, any>,
): void {
  const items = mgr.getParentItemTypes();
  items.forEach((item) => {
    let children = CustomChildItemTypesCache.value[item];
    if (!children) {
      children = [];
      CustomChildItemTypesCache.value[item] = children;
    }
    children.push(itemType);
  });
}

function getCustomChildItemTypes(itemType: ItemType): ItemType[] {
  return (CustomChildItemTypesCache.value[itemType] ?? []) as ItemType[];
}

const unknownManager = (
  itemType: ComponentItemType,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ComponentManager<any, any> => {
  return {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ...baseComponentManager<any, any>({} as unknown as Platform, itemType),
    type: itemType,
    name: itemType,
    isChildAllowed: () => false,
    defaultValue: { responsive: {} },
    defaultChildren: [],
    props: {},
    getDisplayText: () => 'Unknown !',
    category: 'Unknown',
    Component: UnknownComponent,
    DEVComponent: UnknownComponent,
    allowClick: true,
  };
};

function replaceOldNames(type: string): string {
  // replace old names with new names
  switch (type) {
    case 'GridContainer':
      type = 'RowPanel';
      break;
    case 'GridCell':
      type = 'ColumnPanel';
      break;
    case 'CSSGrid':
      type = 'FlexPanel';
      break;
    case 'CSSGridCell':
      type = 'FlexItem';
      break;
    case 'DataTable':
      type = 'DataGrid';
      break;
    default:
  }
  return type;
}

const getManager = (type: string): AnyManager => {
  type = replaceOldNames(type);
  const mgr = ComponentsCache.value[type];
  if (!mgr) {
    // @ts-ignore unknown type
    return unknownManager(type);
  }
  return mgr;
};

export const hasManager = (type: string): boolean => {
  type = replaceOldNames(type);
  const mgr = ComponentsCache.value[type];
  return !!mgr;
};

const useManager = (type: ItemType): AnyManager => {
  useUpdateOnHMR();
  return getManager(type);
};

const getName = (type: string): string => {
  return ComponentNameCache.value[type] ?? `Unknown ${type}`;
};

const getAllManagers = (): AnyManager[] => {
  return Object.values(ComponentsCache.value);
};

const getComponentNameMap = () => {
  return ComponentNameCache.value;
};

export {
  deregisterCustomComponents,
  getAllManagers,
  getComponentNameMap,
  getCustomChildItemTypes,
  getManager,
  getName,
  registerManager,
  registerParentItemTypes,
  unknownManager,
  useManager,
};
